import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Plus, Sparkles, Check } from "lucide-react";
import { useCart } from "../context/CartContext";
import { formatPrice } from "../lib/format";
import api from "../lib/api";

/**
 * Frequently Bought Together — leverages the existing auto-bundle discount system
 * (-10% for 2 distinct, -15% for 3 distinct) by exposing the most attractive
 * combination right on the Product Detail Page.
 */
export default function FrequentlyBoughtBundle({ product }) {
  const { add, items: cartItems } = useCart();
  const [recos, setRecos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState({}); // {productId: bool}

  useEffect(() => {
    if (!product?.id) return;
    let cancelled = false;
    setLoading(true);
    api
      .post("/cart/recommendations", { product_ids: [product.id], limit: 2 })
      .then((r) => {
        if (cancelled) return;
        const data = Array.isArray(r.data) ? r.data : [];
        setRecos(data);
        // Pre-check all recommended items by default
        const initial = {};
        data.forEach((p) => {
          initial[p.id] = true;
        });
        setSelected(initial);
      })
      .catch(() => !cancelled && setRecos([]))
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [product?.id]);

  // The anchor product is always part of the bundle (locked)
  const allProducts = useMemo(() => [product, ...recos], [product, recos]);
  const selectedProducts = useMemo(
    () => allProducts.filter((p) => p && (p.id === product.id || selected[p.id])),
    [allProducts, selected, product.id]
  );

  const distinct = selectedProducts.length;
  const subtotal = selectedProducts.reduce((s, p) => s + (p?.price || 0), 0);
  // Mirror backend _compute_bundle_discount logic
  let bundleRate = 0;
  let bundleLabel = "";
  if (distinct >= 3) {
    bundleRate = 0.15;
    bundleLabel = "Pack Bureau Zen –15%";
  } else if (distinct >= 2) {
    bundleRate = 0.10;
    bundleLabel = "Duo malin –10%";
  }
  const savings = +(subtotal * bundleRate).toFixed(2);
  const total = +(subtotal - savings).toFixed(2);

  const handleAddBundle = () => {
    const inCart = new Set(cartItems.map((c) => c.product_id));
    let added = 0;
    selectedProducts.forEach((p) => {
      if (!p || inCart.has(p.id)) return;
      add(p, 1);
      added += 1;
    });
    // If everything was already in cart, still trigger a visual confirmation
    if (added === 0 && selectedProducts.length > 0) {
      add(selectedProducts[0], 1);
    }
  };

  // Hide the section entirely if there are no recommendations to suggest
  if (!loading && recos.length === 0) return null;

  return (
    <section
      className="mt-16 border-t border-white/10 pt-12"
      data-testid="frequently-bought-bundle"
    >
      <div className="flex items-center gap-2 mb-4">
        <Sparkles size={16} strokeWidth={1.5} className="text-[#FFD700]" />
        <div className="text-xs font-bold uppercase tracking-[0.3em] text-[#C5CDD8]">
          Achetés ensemble
        </div>
      </div>
      <h2 className="font-display text-2xl sm:text-3xl font-bold uppercase tracking-tighter text-white">
        Composez votre pack — économisez jusqu'à 15&nbsp;%
      </h2>
      <p className="mt-2 text-sm text-white/55 max-w-2xl">
        Cochez les articles à associer. Remise pack appliquée automatiquement au panier.
      </p>

      <div className="mt-8 grid lg:grid-cols-5 gap-8 items-start">
        {/* Visual stack with "+" separators */}
        <div className="lg:col-span-3" data-testid="bundle-visual">
          {loading ? (
            <div className="flex items-center gap-3">
              {[0, 1, 2].map((i) => (
                <div
                  key={i}
                  className="flex-1 aspect-square bg-[#0A2A4F] border border-white/5 animate-pulse"
                />
              ))}
            </div>
          ) : (
            <div className="flex items-stretch gap-2 sm:gap-3">
              {allProducts.map((p, i) => (
                <BundleVisual
                  key={p.id}
                  product={p}
                  isAnchor={i === 0}
                  isActive={p.id === product.id || !!selected[p.id]}
                  withPlus={i > 0}
                />
              ))}
            </div>
          )}

          {/* Checkbox list */}
          <ul className="mt-6 divide-y divide-white/10 border-t border-b border-white/10">
            {allProducts.map((p, i) => {
              const isAnchor = i === 0;
              const checked = isAnchor ? true : !!selected[p.id];
              return (
                <li key={p.id} className="flex items-center gap-4 py-4">
                  <button
                    type="button"
                    onClick={() =>
                      !isAnchor &&
                      setSelected((s) => ({ ...s, [p.id]: !s[p.id] }))
                    }
                    disabled={isAnchor}
                    aria-label={
                      isAnchor
                        ? "Produit principal (verrouillé)"
                        : `Inclure ${p.name}`
                    }
                    className={`w-5 h-5 border flex items-center justify-center transition-colors flex-shrink-0 ${
                      checked
                        ? "bg-[#FFD700] border-[#FFD700] text-[#001F3F]"
                        : "border-white/40 bg-transparent text-transparent hover:border-white/70"
                    } ${isAnchor ? "cursor-not-allowed opacity-90" : "cursor-pointer"}`}
                    data-testid={`bundle-toggle-${p.id}`}
                  >
                    {checked && <Check size={12} strokeWidth={3} />}
                  </button>
                  <Link
                    to={`/product/${p.id}`}
                    className="text-sm text-white hover:underline underline-offset-4 flex-1 min-w-0 truncate"
                  >
                    {isAnchor && (
                      <span className="text-[10px] font-bold uppercase tracking-widest text-[#FFD700] mr-2">
                        Ce produit
                      </span>
                    )}
                    {p.name}
                  </Link>
                  <span className="text-sm font-bold text-white whitespace-nowrap">
                    {formatPrice(p.price)}
                  </span>
                </li>
              );
            })}
          </ul>
        </div>

        {/* Pricing summary panel */}
        <aside
          className="lg:col-span-2 border border-white/10 bg-[#0A2A4F] p-6 sm:p-7 lg:sticky lg:top-28"
          data-testid="bundle-summary"
        >
          <div className="text-xs font-bold uppercase tracking-[0.3em] text-[#C5CDD8]">
            Total du pack
          </div>

          <div className="mt-5 space-y-2 text-sm">
            <div className="flex justify-between">
              <span className="text-white/60">
                {distinct} article{distinct > 1 ? "s" : ""}
              </span>
              <span className="text-white">{formatPrice(subtotal)}</span>
            </div>
            {bundleRate > 0 && (
              <div className="flex justify-between" data-testid="bundle-savings-line">
                <span className="text-[#A6E0B5]">{bundleLabel}</span>
                <span className="text-[#A6E0B5] font-bold">
                  − {formatPrice(savings)}
                </span>
              </div>
            )}
          </div>

          <div className="mt-5 pt-5 border-t border-white/10 flex items-baseline justify-between">
            <span className="text-xs font-bold uppercase tracking-widest text-white/80">
              À payer
            </span>
            <span
              className="font-display text-3xl font-bold text-white"
              data-testid="bundle-total"
            >
              {formatPrice(total)}
            </span>
          </div>

          {bundleRate > 0 ? (
            <div className="mt-3 inline-flex items-center gap-1.5 bg-[#FFD700] text-[#001F3F] text-[10px] font-bold uppercase tracking-[0.25em] px-2.5 py-1">
              ★ Vous économisez {formatPrice(savings)}
            </div>
          ) : (
            <div className="mt-3 text-[10px] text-white/45 uppercase tracking-widest">
              Ajoutez un 2ème produit pour débloquer −10 %
            </div>
          )}

          <button
            type="button"
            onClick={handleAddBundle}
            disabled={distinct === 0 || product.stock === 0}
            className="mt-6 w-full border border-white bg-white text-[#001F3F] hover:bg-[#FFD700] hover:border-[#FFD700] transition-all uppercase tracking-[0.25em] text-xs font-bold py-4 inline-flex items-center justify-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed"
            data-testid="bundle-add-cta"
          >
            <Plus size={14} strokeWidth={2.5} />
            Ajouter le pack au panier
          </button>

          <p className="mt-3 text-[10px] text-white/40 leading-relaxed">
            Remise appliquée automatiquement dans votre panier — annulable à tout moment.
          </p>
        </aside>
      </div>
    </section>
  );
}

function BundleVisual({ product, isAnchor, isActive, withPlus }) {
  return (
    <>
      {withPlus && (
        <div className="flex items-center justify-center px-1 sm:px-2 text-2xl sm:text-3xl text-white/40 font-light select-none">
          +
        </div>
      )}
      <Link
        to={isAnchor ? "#" : `/product/${product.id}`}
        onClick={(e) => isAnchor && e.preventDefault()}
        className={`flex-1 aspect-square bg-[#0A2A4F] border overflow-hidden block relative transition-all ${
          isActive ? "border-[#FFD700]/60" : "border-white/10 grayscale opacity-50"
        } ${isAnchor ? "cursor-default" : ""}`}
        data-testid={`bundle-visual-${product.id}`}
      >
        <img
          src={product.image}
          alt={product.name}
          className="w-full h-full object-cover"
          loading="lazy"
        />
        {isAnchor && (
          <div className="absolute top-2 left-2 bg-[#FFD700] text-[#001F3F] text-[9px] font-bold uppercase tracking-[0.2em] px-2 py-0.5">
            Ce produit
          </div>
        )}
      </Link>
    </>
  );
}
